import {
  Box,
  CircularProgress,
  FormHelperText,
  InputAdornment,
  Typography,
} from "@mui/material";
import {
  SectionTitle,
  StyledOneChildContainer,
  StyledParentContainer,
  StyledTwoChildContainer,
} from "../../StyledComponents/Container";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../../../store/store";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getSourceList } from "../../services/AutoDebit/AutoDebitServices";
import * as constants from "../../../../constants";
import dayjs from "dayjs";
import ContainedButton from "../../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../../shared/Button/customOutlinedButton";
import CancelPopUp from "../../../PopUp/PopUp";
import Grid from "@mui/material/Grid2";
import CustomTextField from "../../../../shared/CustomTextFeild";
import CustomAutocomplete from "../../../../shared/AutoComplete";
import CustomDatePicker from "../../../../shared/CustomDate";
import { notifyUser } from "../../Utils/notifyUser";
import { NumericFormat } from "react-number-format";

function formatCurrency(amount: any) {
  return amount?.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const CreditMakePayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();
  const translate = t as (key: string) => string;

  const [sourceList, setSourceList] = useState<any>([]);
  const [openPopUp, setOpenPopUp] = useState(false);

  const [sourceError, setSourceError] = useState<string>("");
  const [amountToPayError, setAmountToPayError] = useState<string>("");
  const [dateError, setDateError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    creditlineId: "",
    name: "",
    projectNumber: "",
    projectDuration: "",
    interestRate: "",
    description: "",
    type: "",
    status: "",
    availableCredit: "",
    participantId: "",
    clientId: "",
    version: "",
    fundId: 0,
    source: "",
    paymentSourceAccountAvailableBalance: "",
    amountPaid: "",
    outstandingBalance: "",
    paymentDueDate: "",
    creditlineTransactionHistory: [
      {
        availableCredit: "",
        amount: "",
        glCategory: "Liabilities",
        transactionType: "CR",
        transactionDate: new Date().toISOString(),
        paymentEffectiveDate: new Date().toISOString(),
        paymentDate: new Date().toISOString(),
      },
    ],
  });

  const handleSourceChange = (event: any, newValue: any) => {
    if (newValue) {
      setFormData((prev) => ({
        ...prev,
        fundId: newValue.fundId,
        source: newValue.fundName,
        paymentSourceAccountAvailableBalance: newValue.availableBalance,
      }));
      setSourceError("");
    } else {
      setFormData((prev) => ({
        ...prev,
        fundId: 0,
        source: "",
        paymentSourceAccountAvailableBalance: "",
      }));
      setSourceError(translate("pleaseSelectSource"));
    }
  };

  const handleAmountToPayChange = (event: any) => {
    const value = event.target.value;
    const regex = /^\d+(\.\d{0,2})?$/;

    if (!value) {
      setFormData((prev) => ({
        ...prev,
        amountPaid: "",
        creditlineTransactionHistory: [
          {
            ...prev.creditlineTransactionHistory[0],
            amount: "",
          },
          ...prev.creditlineTransactionHistory.slice(1),
        ],
      }));
      setAmountToPayError(translate("pleaseEnteraValidAmountToPay"));
    } else if (!regex.test(value)) {
      setAmountToPayError(translate("amountIslimitedTo2DecimalPlaces"));
    } else {
      setFormData((prev) => ({
        ...prev,
        amountPaid: value,
        creditlineTransactionHistory: [
          {
            ...prev.creditlineTransactionHistory[0],
            amount: value,
          },
          ...prev.creditlineTransactionHistory.slice(1),
        ],
      }));
      setAmountToPayError("");
    }
  };

  const handleDateChange = (newValue: dayjs.Dayjs | null) => {
    if (!newValue) {
      setDateError(translate("PaymentEffectiveDateIsRequired"));
    } else {
      setFormData((prev) => ({
        ...prev,
        creditlineTransactionHistory: [
          {
            ...prev.creditlineTransactionHistory[0],
            paymentEffectiveDate: newValue.toISOString(),
          },
          ...prev.creditlineTransactionHistory.slice(1),
        ],
      }));
      setDateError("");
    }
  };

  const handleCancel = () => {
    setOpenPopUp(true);
  };
  const handlePopUpClose = () => {
    setOpenPopUp(false);
  };
  function handleConfirm() {
    navigate("/creditLineSummaryListing");
  }
  const handleContinue = () => {
    if (handleValidation()) {
      navigate("/creditLinePaymentSubmit", { state: formData });
    }
  };

  const handleValidation = () => {
    let isValid = true;

    setSourceError("");
    setAmountToPayError("");
    const amount = parseFloat(formData.amountPaid);
    const availBalance = formData.paymentSourceAccountAvailableBalance
      ? Number(formData.paymentSourceAccountAvailableBalance)
      : 0;

    if (!formData.source) {
      setSourceError(translate("pleaseSelectSource"));
      isValid = false;
    } else if (
      formData.source != "Checking" &&
      amount &&
      amount > availBalance
    ) {
      setSourceError(translate("Insufficient Fund Balance!"));
      isValid = false;
    }
    if (isNaN(amount) || amount <= 0) {
      setAmountToPayError(translate("pleaseEnteraValidAmountToPay"));
      isValid = false;
    } else if (
      amount > parseFloat(formData.outstandingBalance.replace(/,/g, ""))
    ) {
      setAmountToPayError(
        translate("PaymentAmountCannotBeGreaterThanCreditBalance")
      );
      isValid = false;
    }

    return isValid;
  };
  async function getLoanSourceList(pId: number, clientId: number) {
    setIsLoading(true);
    try {
      const response = await getSourceList(pId, clientId);

      if (response.status === constants.STATUS_CODES.SUCCESS) {
        const { cashAccounts, funds } = response.data.data;

        const cashAccountEntry = cashAccounts?.fundName
          ? {
            fundId: cashAccounts.fundId,
            fundName: cashAccounts.fundName,
            fundType: cashAccounts.fundType,
            description: cashAccounts.description,
            availableBalance: cashAccounts.availableBalance,
          }
          : null;

        const mappedFunds = (funds || []).filter((fund: any) => fund?.isActive === constants.IS_ACTIVE_YES)
        .map((fund: any) => ({
          fundId: fund.fundId,
          fundName: fund.fundName,
          fundType: fund.fundType,
          description: fund.description,
          availableBalance: fund.availableBalance,
        }));

        // ✅ Build final list conditionally
        const finalSourceList = [
          ...(cashAccountEntry ? [cashAccountEntry] : []),
          ...mappedFunds,
        ];
        setSourceList(finalSourceList);

      } else {
        notifyUser(
          response.response?.data?.message || translate("failedToFetchFundSources"), "error", translate("makePayment")
        );
      }
    } catch (error: any) {
      notifyUser(
        error.response?.data?.message || translate("failedToFetchFundSources:"), "error", translate("makePayment")
      );
    } finally {
      setIsLoading(false);
    }
  }


  useEffect(() => {
    getLoanSourceList(userInfo.defaultParticipantId, userInfo.clientId);
    if (location.state.submitData) {
      const data = location.state.submitData;
      setFormData(data);
    }
    if (location?.state?.rowData) {
      const data = location?.state?.rowData;
      setFormData((prev) => ({
        ...prev,
        creditlineId: data.creditlineId,
        name: data.name,
        projectNumber: data.projectNumber,
        projectDuration: data.projectDuration,
        interestRate: data.interestRate,
        description: data.description,
        type: data.type,
        availableCredit: data.availableCredit.replace(/[$,]/g, ""),
        status: data.status,
        participantId: data.participantId,
        clientId: data.clientId,
        version: data.version,
        fundId: data.fundId,
        outstandingBalance: data.outstandingBalance.replace("$", ""),
        creditlineTransactionHistory: [
          {
            ...prev.creditlineTransactionHistory[0],
            availableCredit: data.availableCredit.replace(/[$,]/g, ""),
          },
          ...prev.creditlineTransactionHistory.slice(1),
        ],
      }));
    }
  }, []);

  return (
    <StyledParentContainer>
      <StyledOneChildContainer>
        <StyledTwoChildContainer sx={{ border: "none" }}>
          <SectionTitle>
            {translate("makePayment")}: {formData?.name}
          </SectionTitle>
          <StyledTwoChildContainer>
            {!isLoading ? (
              <Box sx={{ width: "100%", padding: "40px" }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <CustomTextField
                      labelText={translate("creditLineBalance")}
                      value={formData.outstandingBalance}
                      readOnly={true}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, sm: 0, lg: 8 }}
                    sx={{ display: { lg: "block", sm: "none", xs: "block" } }}
                  ></Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <CustomTextField
                      labelText={translate("amountDue")}
                      value={formData.outstandingBalance}
                      readOnly={true}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, sm: 0, lg: 8 }}
                    sx={{ display: { lg: "block", sm: "none", xs: "block" } }}
                  ></Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <>
                      <CustomAutocomplete
                        labelText={translate("source")}
                        required
                        value={
                          sourceList.find(
                            (item: any) => item.fundName === formData.source
                          ) || null
                        }
                        onChange={handleSourceChange}
                        options={sourceList}
                        getOptionLabel={(option) => `${option.fundName}`}
                        placeholder={translate("selectSource")}
                        error={!!sourceError}
                      />
                      {sourceError && (
                        <FormHelperText error>{sourceError}</FormHelperText>
                      )}
                    </>
                  </Grid>
                  <Grid
                    size={{ xs: 12, sm: 1, lg: 2 }}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignContent: "flex-end",
                      mb: sourceError ? 2.5 : 0,
                    }}
                  >
                    {formData.paymentSourceAccountAvailableBalance && (
                      <Typography>
                        {formatCurrency(
                          formData.paymentSourceAccountAvailableBalance
                        )}
                      </Typography>
                    )}
                  </Grid>
                  <Grid
                    size={{ xs: 12, sm: 0, lg: 4 }}
                    sx={{ display: { lg: "block", sm: "none", xs: "block" } }}
                  ></Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <>
                      <NumericFormat
                        customInput={CustomTextField}
                        id="amount-to-pay"
                        required
                        labelText={translate("amountToPay")}
                        placeholder={translate("amount")}
                        thousandSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        value={formData.amountPaid || ""}
                        isAllowed={(values) => {
                          const { floatValue } = values;
                          return (
                            floatValue === undefined ||
                            floatValue <= constants.MAX_AMOUNT_ALLOWED
                          );
                        }}
                        onValueChange={(values) => {
                          const { floatValue } = values;
                          handleAmountToPayChange({
                            target: {
                              name: "amountPaid",
                              value: floatValue,
                            },
                          });
                        }}
                        prefix="$"
                        error={!!amountToPayError}
                      />
                      {amountToPayError && (
                        <FormHelperText sx={{ mt: -1 }} error>
                          {amountToPayError}
                        </FormHelperText>
                      )}
                    </>
                  </Grid>
                  <Grid
                    size={{ xs: 12, sm: 0, lg: 8 }}
                    sx={{ display: { lg: "block", sm: "none", xs: "block" } }}
                  ></Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <>
                      <CustomDatePicker
                        labelText={translate("paymentEffectiveDate")}
                        value={
                          dayjs(
                            formData.creditlineTransactionHistory[0]
                              ?.paymentEffectiveDate
                          )
                            ? dayjs(
                              formData.creditlineTransactionHistory[0]
                                ?.paymentEffectiveDate
                            )
                            : dayjs()
                        }
                        onChange={handleDateChange}
                        error={!!dateError}
                        readOnly
                      />
                      {dateError && (
                        <FormHelperText error>{dateError}</FormHelperText>
                      )}
                    </>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: "200px",
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </StyledTwoChildContainer>
          <div className="right-fields" style={{ marginTop: "10px" }}>
            <ContainedButton sx={{ mr: 2 }} onClick={handleContinue}>
              {translate("continue")}{" "}
            </ContainedButton>
            <OutlinedButton onClick={handleCancel}>
              {translate("cancel")}
            </OutlinedButton>
          </div>
          <CancelPopUp
            open={openPopUp}
            popupTitle={translate("confirmCancel")}
            handleClose={handlePopUpClose}
            saveChanges={() => handleConfirm()}
            message={translate("doYouWantToCancelChanges?")}
          />
        </StyledTwoChildContainer>
      </StyledOneChildContainer>
    </StyledParentContainer>
  );
};

export default CreditMakePayment;
